// v2
import React, { useState, useRef, useCallback, useEffect, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { TaskFull, ReminderOut } from '@/redux/internal-services/workspaceApiExtensions';
import { useUpdateTaskStatusMutation, useUpdateTaskMutation, useClaimTaskMutation, useFollowTaskMutation, useUnfollowTaskMutation, useGetTaskFollowersQuery, useReassignTaskMutation, useGetRemindersQuery, useCreateReminderMutation, useCancelReminderMutation, useGetTaskCommentsQuery, useAddTaskCommentMutation } from '@/redux/internal-services/workspaceApiExtensions';
import { useGetTenantUserByIdQuery, useListTenantUsersQuery, useListAssignableRolesQuery } from '@/redux/internal-services/onboardingApi';
import { useAppSelector, useSamSurfaceContext } from '@/hooks';
import { SwipeHint, ExpandChevron } from '@/components/UI/SwipeHint';

// Portalled menus use inline styles, so they take the numeric value of Tailwind's `z-popover`.
const POPOVER_Z = 70;

function useDebounce<T>(value: T, ms: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debouncedValue;
}

// ─── Priority ─────────────────────────────────────────────────────────────────

// Warning triangle SVG — used for High (orange) and Urgent (red) only
const WarningTriangle = ({ className }: { className?: string }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className={`flex-shrink-0 ${className ?? ''}`}>
    <path d="M12 3 1.8 20.5h20.4L12 3zm0 6.2 4.9 8.5H7.1L12 9.2zM11 12h2v3h-2v-3zm0 4h2v2h-2v-2z"/>
  </svg>
);

// PriorityIcon kept for the priority picker dropdown
export const PriorityIcon = ({ level, size = 10 }: { level: string; size?: number }) => {
  if (level === 'urgent') return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-danger-text"><path d="M12 3 1.8 20.5h20.4L12 3zm0 6.2 4.9 8.5H7.1L12 9.2zM11 12h2v3h-2v-3zm0 4h2v2h-2v-2z"/></svg>;
  if (level === 'high') return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="text-warning-text"><path d="M12 3 1.8 20.5h20.4L12 3zm0 6.2 4.9 8.5H7.1L12 9.2zM11 12h2v3h-2v-3zm0 4h2v2h-2v-2z"/></svg>;
  return <span style={{ width: size, height: size }} className={`inline-block rounded-full flex-shrink-0 ${level === 'normal' ? 'bg-ui-border3' : 'bg-ui-border'}`} />;
};

// `tone` colours the priority button and picker rows; `text`/`bar` are used by the other task surfaces.
export const PRIORITY_CFG = {
  low:    { label: 'Low',    tone: 'text-ui-text3',     text: 'text-ui-text3',     bar: 'bg-ui-border3' },
  normal: { label: 'Normal', tone: 'text-ui-text3',     text: 'text-accent-text',  bar: 'bg-accent/25' },
  high:   { label: 'High',   tone: 'text-warning-text', text: 'text-warning-text', bar: 'bg-warning' },
  urgent: { label: 'Urgent', tone: 'text-danger-text',  text: 'text-danger-text',  bar: 'bg-danger' },
} as const;

export const TYPE_LABEL: Record<string, string> = {
  follow_up: 'Follow up', approval: 'Approval', review: 'Review',
};

const THRESHOLD = 80;

// ─── Task type icon ───────────────────────────────────────────────────────────

const TaskTypeIcon = ({ type, faded }: { type?: string | null; faded: boolean }) => {
  const svgProps = {
    width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none' as const,
    stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const,
    className: `flex-shrink-0 mt-px ${faded ? 'text-ui-border3' : ''}`,
  };
  const t = (type ?? '').toLowerCase();
  if (t === 'call' || t === 'phone' || t === 'phone_call') return (
    <svg {...svgProps}><path d="M6.6 3.5h3l1.5 3.8-2 1.2a12 12 0 0 0 5.4 5.4l1.2-2 3.8 1.5v3a1.8 1.8 0 0 1-2 1.8A15.6 15.6 0 0 1 4.8 5.5a1.8 1.8 0 0 1 1.8-2z"/></svg>
  );
  if (t === 'site_visit' || t === 'visit' || t === 'inspection') return (
    <svg {...svgProps}><path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z"/><circle cx="12" cy="10" r="2.6"/></svg>
  );
  if (t === 'estimate' || t === 'file' || t === 'upload') return (
    <svg {...svgProps}><path d="M4 20V6.5A2.5 2.5 0 0 1 6.5 4H14l6 6v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z"/><path d="M13.6 4.2V10H19"/></svg>
  );
  if (t === 'approval') return (
    <svg {...svgProps}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
  );
  // document / permit / contract / review / default
  return (
    <svg {...svgProps}><rect x="4" y="3.5" width="16" height="17" rx="3"/><path d="M8 8.5h8M8 12.5h8M8 16.5h4"/></svg>
  );
};

// ─── Due date formatting ──────────────────────────────────────────────────────

type DueDisplay = { main: string; rel?: string; isOverdue: boolean };

function getDueDisplay(due: Date, status: string): DueDisplay {
  const now = new Date();
  const timeStr = due.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayDiff = Math.round((dueDay.getTime() - todayDay.getTime()) / 86400000);
  const isOverdue = due < now && !['completed', 'cancelled'].includes(status);

  if (isOverdue) {
    const diffMs = now.getTime() - due.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffH = Math.round(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    const rel = diffMin < 60 ? `${diffMin}m ago` : diffH < 24 ? `${diffH}h ago` : `${diffD} day${diffD !== 1 ? 's' : ''} ago`;
    if (dayDiff === 0) return { main: `Was due today ${timeStr}`, rel, isOverdue: true };
    const wd = due.toLocaleDateString(undefined, { weekday: 'short' });
    const mo = due.toLocaleDateString(undefined, { month: 'short' });
    return { main: `Was due ${wd} ${due.getDate()} ${mo} ${timeStr}`, rel, isOverdue: true };
  }

  if (dayDiff === 0) {
    const diffMs = due.getTime() - now.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffH = Math.round(diffMs / 3600000);
    const rel = diffMin < 60 ? `in ${diffMin}m` : `in ${diffH}h`;
    return { main: `Due today ${timeStr}`, rel, isOverdue: false };
  }
  if (dayDiff === 1) return { main: `Due tomorrow ${timeStr}`, isOverdue: false };
  const wd = due.toLocaleDateString(undefined, { weekday: 'short' });
  const mo = due.toLocaleDateString(undefined, { month: 'short' });
  return { main: `Due ${wd} ${due.getDate()} ${mo} ${timeStr}`, isOverdue: false };
}

// ─── Entity route ─────────────────────────────────────────────────────────────

export const ENTITY_ROUTE: Record<string, (id: string) => string> = {
  engagement:  (id) => `/chat?engagement=${id}`,
  property:    (id) => `/chat?property=${id}`,
  job:         (id) => `/chat?job=${id}`,
  transaction: (id) => `/chat?transaction=${id}`,
  contact:     (id) => `/chat?contact=${id}`,
};

export const ContextTypeIcon = ({ type, className }: { type?: string | null; className?: string }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`flex-shrink-0 ${className ?? ''}`}>
    {type === 'property' ? <><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></> :
     type === 'job' ? <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></> :
     <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></>}
  </svg>
);

const IcoChevron = ({ open }: { open: boolean }) => <ExpandChevron open={open} className="flex-shrink-0" />;

const IcoCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);


// ─── Expanded inline detail ───────────────────────────────────────────────────

function TaskInlineDetail({ task, currentUserId, isClosed }: { task: TaskFull; currentUserId: string; isClosed: boolean }) {
  const [reassignTask, { isLoading: reassigning }] = useReassignTaskMutation();
  const [createReminder, { isLoading: creatingReminder }] = useCreateReminderMutation();
  const [cancelReminder] = useCancelReminderMutation();
  const [followTask] = useFollowTaskMutation();
  const [unfollowTask] = useUnfollowTaskMutation();
  const { data: followers = [] } = useGetTaskFollowersQuery(task.task_id);
  // Assignee and follower are separate roles; legacy tasks may still list the assignee as a follower.
  const watcherCount = followers.filter(id => id !== task.assignee_user_id).length;
  const { data: comments = [] } = useGetTaskCommentsQuery(task.task_id);
  const [addComment, { isLoading: addingComment }] = useAddTaskCommentMutation();
  const { data: assigneeUser } = useGetTenantUserByIdQuery(
    task.assignee_user_id!,
    { skip: !task.assignee_user_id || task.assignee_user_id === currentUserId },
  );

  const [showReassign, setShowReassign] = useState<'user' | 'queue' | false>(false);
  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [showReminderInput, setShowReminderInput] = useState(false);
  const [reminderDt, setReminderDt] = useState('');
  const [reassignError, setReassignError] = useState('');
  const reminderPanelRef = useRef<HTMLDivElement>(null);
  const [userSearch, setUserSearch] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const debouncedUserSearch = useDebounce(userSearch, 300);
  const assigneeRowRef = useRef<HTMLDivElement>(null);
  const reassignPopoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  const { data: usersRes } = useListTenantUsersQuery(
    showReassign === 'user'
      ? { page_size: 50, ...(debouncedUserSearch ? { search: debouncedUserSearch } : {}) }
      : { page_size: 0 },
    { skip: showReassign !== 'user' },
  );
  const { data: rolesRes } = useListAssignableRolesQuery();
  const { data: remindersRes } = useGetRemindersQuery({ source_entity_id: task.task_id });

  const handleToggleReassign = () => {
    if (assigneeRowRef.current) {
      const rect = assigneeRowRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const upward = spaceBelow < 220;
      setPopoverStyle({
        position: 'fixed', width: 180, zIndex: POPOVER_Z,
        left: Math.min(rect.left, window.innerWidth - 188),
        ...(upward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    }
    setShowReassign(prev => prev ? false : 'user');
    setUserSearch('');
  };

  useEffect(() => {
    if (!showReassign) return;
    const handler = (e: MouseEvent) => {
      if (!reassignPopoverRef.current?.contains(e.target as Node) &&
          !assigneeRowRef.current?.contains(e.target as Node)) {
        setShowReassign(false); setUserSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showReassign]);

  useEffect(() => {
    if (!showReminderPanel) return;
    const handler = (e: MouseEvent) => {
      if (reminderPanelRef.current && !reminderPanelRef.current.contains(e.target as Node)) {
        setShowReminderPanel(false);
        setShowReminderInput(false);
        setReminderDt('');
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [showReminderPanel]);

  const allUsers = usersRes?.data ?? [];
  const allRoles = (rolesRes && 'data' in rolesRes ? rolesRes.data : rolesRes) ?? [];
  const filteredUsers = allUsers;
  const filteredRoles = userSearch
    ? allRoles.filter(r => r.name.toLowerCase().includes(userSearch.toLowerCase()))
    : allRoles;

  const isFollowing = followers.includes(currentUserId);
  const reminders = (remindersRes?.data ?? []).filter(r => ['scheduled', 'snoozed'].includes(r.status));

  const formatFireTime = (r: ReminderOut) => {
    const fireOn = r.next_fire_on ?? r.dtstart;
    if (!fireOn) return 'Pending';
    const d = new Date(fireOn);
    const now = new Date();
    return d.toDateString() === now.toDateString()
      ? `Today ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
      : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const isAssignedToMe = task.assignee_user_id === currentUserId;
  // Backend-resolved name first — /roles/assignable is 403 for viewers without role:read.
  const queueRoleName = !task.assignee_user_id && task.queue_role_id
    ? (task.queue_role_name ?? allRoles.find(r => r.id === task.queue_role_id)?.name ?? null) : null;
  const assigneeName = task.assignee_user_id
    ? (isAssignedToMe ? 'You' : (assigneeUser?.name ?? assigneeUser?.email ?? 'Assigned'))
    : (queueRoleName ? `${queueRoleName} Queue` : 'Unassigned');
  const rawName = isAssignedToMe ? 'You' : (assigneeUser?.name ?? assigneeUser?.email ?? '');
  const assigneeInitials = rawName
    ? rawName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : (queueRoleName ? '☰' : '?');

  const toggleFollow = async () => {
    try {
      if (isFollowing) await unfollowTask(task.task_id).unwrap();
      else await followTask(task.task_id).unwrap();
    } catch { /* non-critical */ }
  };

  const XRow = ({ label: _label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center gap-2 min-h-[28px]">
      {children}
    </div>
  );

  return (
    <div className="mt-2.5 pt-2.5 border-t border-ui-border/60 space-y-1"
      onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>

      {/* ── Assigned row ── */}
      <XRow label="Assigned">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 leading-none ${isClosed ? 'bg-ui-hover text-ui-text3' : 'bg-accent/15 text-accent-text'}`}>
            {assigneeInitials}
          </div>
          <span className="text-xs font-medium text-ui-text truncate">{assigneeName}</span>
          {!isClosed && (
            <div ref={assigneeRowRef} className="flex-shrink-0">
              <button onClick={handleToggleReassign}
                className={`text-xs font-semibold px-2 py-0.5 rounded-md border transition-colors ${showReassign ? 'border-accent/30 bg-accent/5 text-accent-text' : 'border-ui-border text-ui-text3 hover:bg-ui-hover hover:text-ui-text'}`}>
                Reassign
              </button>
            </div>
          )}
          {reassigning && (
            <svg className="animate-spin text-accent-text/40 flex-shrink-0" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
            </svg>
          )}
        </div>
        {!isClosed && !queueRoleName && (
          <>
            {watcherCount > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-md border flex-shrink-0 border-ui-border text-ui-text3">
                {watcherCount} Follower{watcherCount !== 1 ? 's' : ''}
              </span>
            )}
            {/* Assignee is a separate role from follower (TSK-FR-04-012) — no follow toggle for them. Owner sees toggle when assigned to someone else. */}
            {!isAssignedToMe && <button onClick={toggleFollow}
              className={`text-xs font-semibold px-2 py-0.5 rounded-md border flex-shrink-0 transition-colors ${
                isFollowing
                  ? 'border-danger/30 bg-danger/10 text-danger-text hover:bg-danger/15'
                  : 'border-ui-border text-ui-text3 hover:border-accent/30 hover:text-accent-text'
              }`}>
              {isFollowing ? 'Unfollow' : 'Follow'}
            </button>}
          </>
        )}
      </XRow>

      {reassignError && <p className="text-xs text-danger-text pl-[72px]">{reassignError}</p>}

      {/* ── Reminders — chips + popup panel ─── */}
      {!isClosed && (
        <div className="relative" ref={reminderPanelRef}>
          {/* Chip row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {reminders.slice(0, 2).map(r => (
              <button key={r.id} type="button"
                onClick={() => setShowReminderPanel(v => !v)}
                className="inline-flex items-center gap-1 rounded-full bg-ui-subtle border border-ui-border px-2 py-0.5 text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className="flex-shrink-0 text-ui-text3">
                  <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
                </svg>
                {formatFireTime(r)}
              </button>
            ))}
            {reminders.length > 2 && (
              <button type="button" onClick={() => setShowReminderPanel(v => !v)}
                className="inline-flex items-center rounded-full bg-ui-subtle border border-ui-border px-2 py-0.5 text-xs font-medium text-ui-text2 hover:bg-ui-hover transition-colors">
                +{reminders.length - 2}
              </button>
            )}
            {reminders.length === 0 ? (
              <button type="button" onClick={() => { setShowReminderPanel(true); setShowReminderInput(true); }}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-ui-border3 px-2 py-0.5 text-xs text-ui-text3 hover:text-ui-text hover:border-accent/30 transition-colors">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="flex-shrink-0">
                  <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
                </svg>
                Add reminder
              </button>
            ) : (
              <button type="button" onClick={() => { setShowReminderPanel(true); setShowReminderInput(true); }}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-dashed border-ui-border3 text-ui-text3 hover:text-ui-text hover:border-accent/30 transition-colors flex-shrink-0">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            )}
          </div>

          {/* Reminder panel */}
          {showReminderPanel && (
            <>
            <div className="fixed inset-0 z-40" onMouseDown={e => { e.stopPropagation(); setShowReminderPanel(false); setShowReminderInput(false); setReminderDt(''); }} />
            <div className="mt-1.5 rounded-xl border border-ui-border bg-ui-card shadow-md overflow-hidden relative z-50">
              {/* Header */}
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-ui-border/70">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className="text-warning-text flex-shrink-0">
                  <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
                </svg>
                <span className="text-xs font-semibold text-ui-text">Reminders</span>
                <span className="flex-1"/>
                {reminders.length > 0 && (
                  <span className="text-xs text-ui-text3">{reminders.length} set</span>
                )}
              </div>

              {/* Reminder rows */}
              {reminders.map(r => {
                const isRecurring = !!(r as any).rrule;
                return (
                  <div key={r.id} className="flex items-center gap-2 px-3 py-2 border-b border-ui-border/40">
                    {isRecurring ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="flex-shrink-0 text-accent-text">
                        <path d="M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6"/>
                        <path d="M17.5 3v3.8h-3.8M6.5 21v-3.8h3.8"/>
                      </svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="flex-shrink-0 text-accent-text">
                        <circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>
                      </svg>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-ui-text truncate">{formatFireTime(r)}</p>
                      <p className="text-xs text-ui-text3">{isRecurring ? 'Recurring' : 'One time'}</p>
                    </div>
                    <button type="button" onClick={() => cancelReminder(r.id)} title="Remove"
                      className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-ui-text3/60 hover:text-danger-text hover:bg-danger/10 transition-colors">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                        <path d="M6 6l12 12M18 6 6 18"/>
                      </svg>
                    </button>
                  </div>
                );
              })}

              {/* Add reminder */}
              <div className="px-3 py-2">
                {showReminderInput ? (
                  <div className="flex items-center gap-1.5">
                    <input type="datetime-local" value={reminderDt} onChange={e => setReminderDt(e.target.value)}
                      min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                      autoFocus
                      className="flex-1 h-6 text-micro border border-ui-border rounded-lg px-2 bg-ui-input text-ui-text focus:outline-none focus:ring-1 focus:ring-ui-ring/30"/>
                    <button type="button"
                      onClick={async () => {
                        if (!reminderDt) return;
                        await createReminder({ title: task.title, dtstart: new Date(reminderDt).toISOString(), source_entity_type: 'task', source_entity_id: task.task_id });
                        setReminderDt('');
                        setShowReminderInput(false);
                      }}
                      disabled={!reminderDt || creatingReminder}
                      className="flex-shrink-0 text-xs px-2 h-6 rounded-lg btn-sheen text-on-primary font-semibold disabled:opacity-50 transition-colors">
                      {creatingReminder ? '…' : 'Add'}
                    </button>
                    <button type="button" onClick={() => { setShowReminderInput(false); setReminderDt(''); }}
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-ui-text3 hover:text-ui-text hover:bg-ui-hover transition-colors">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setShowReminderInput(true)}
                    className="flex items-center gap-1.5 text-xs text-ui-text3 hover:text-ui-text transition-colors w-full">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add reminder
                  </button>
                )}
              </div>

            </div>
            </>
          )}
        </div>
      )}

      {/* ── Reassign popover ── */}
      {showReassign && createPortal(
        <div ref={reassignPopoverRef} style={popoverStyle} className="rounded-xl border border-ui-border bg-ui-card shadow-pop overflow-hidden">
          <div className="flex border-b border-ui-border">
            {(['user', 'queue'] as const).map(tab => (
              <button key={tab} onClick={() => { setShowReassign(tab); setUserSearch(''); }}
                className={`flex-1 py-1.5 text-xs font-semibold transition-colors ${
                  showReassign === tab ? 'text-accent-text border-b-2 border-accent bg-accent/5' : 'text-ui-text3 hover:text-ui-text hover:bg-ui-hover'
                }`}>
                {tab === 'user' ? 'Person' : 'Queue'}
              </button>
            ))}
          </div>
          <div className="p-1.5 border-b border-ui-border">
            <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
              placeholder={showReassign === 'user' ? 'Search people…' : 'Search queues…'}
              autoFocus className="w-full text-xs px-2 py-0.5 rounded-md border border-ui-border bg-ui-input text-ui-text focus:outline-none" />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {showReassign === 'user' && filteredUsers.map(u => (
              <button key={u.user_id} onClick={async () => {
                  setReassignError('');
                  try { await reassignTask({ id: task.task_id, assignee_user_id: u.user_id }).unwrap(); setShowReassign(false); setUserSearch(''); }
                  catch { setReassignError('Could not reassign — try again.'); }
                }}
                className={`w-full text-left text-xs px-3 py-1.5 hover:bg-accent/5 transition-colors text-ui-text flex items-center gap-2 ${u.user_id === task.assignee_user_id ? 'font-semibold text-accent-text bg-accent/5' : ''}`}>
                <div className="w-4 h-4 rounded-full bg-accent/10 flex items-center justify-center text-xs font-bold text-accent-text flex-shrink-0">
                  {(u.name || u.email || '?')[0].toUpperCase()}
                </div>
                {u.name || u.email}
              </button>
            ))}
            {showReassign === 'queue' && filteredRoles.map(r => (
              <button key={r.id} onClick={() => { reassignTask({ id: task.task_id, queue_role_id: r.id }); setShowReassign(false); setUserSearch(''); }}
                className={`w-full text-left text-xs px-3 py-1.5 hover:bg-accent/5 transition-colors text-ui-text flex items-center gap-2 ${r.id === task.queue_role_id ? 'font-semibold text-accent-text bg-accent/5' : ''}`}>
                <div className="w-4 h-4 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-text">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                {r.name}
              </button>
            ))}
            {showReassign === 'user' && filteredUsers.length === 0 && (
              <p className="text-xs text-ui-text3 text-center py-3">{debouncedUserSearch ? 'No users found' : 'Loading…'}</p>
            )}
            {showReassign === 'queue' && filteredRoles.length === 0 && (
              <p className="text-xs text-ui-text3 text-center py-3">No queues available</p>
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* ── Comments row ── */}
      <div>
        <XRow label="Comments">
          <button
            className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
            onClick={() => comments.length > 0 && setShowComments(v => !v)}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="flex-shrink-0 text-ui-text3">
              <path d="M4.5 5.5h15a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H10l-4.5 3.5V16.5H4.5A1.5 1.5 0 0 1 3 15V7a1.5 1.5 0 0 1 1.5-1.5z"/>
            </svg>
            <span className="text-xs font-medium text-ui-text">Comments</span>
            {comments.length > 0 && (
              <span className="text-xs font-bold text-accent-text bg-accent/10 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5">
                {comments.length}
              </span>
            )}
            <span className="flex-1" />
          </button>
          {!isClosed && (
            <button title={showCommentForm ? 'Close' : 'Add comment'} onClick={() => { setShowCommentForm(v => !v); if (!showCommentForm) setShowComments(true); }}
              className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${showCommentForm ? 'bg-accent/10 text-accent-text' : 'text-ui-text3/40 hover:text-accent-text hover:bg-accent/[0.06]'}`}>
              {showCommentForm
                ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                : <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
            </button>
          )}
        </XRow>

        {showCommentForm && (
          <div className="mt-1 ml-[72px] flex flex-col gap-1 bg-ui-subtle rounded-xl px-2 py-2">
            <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
              placeholder="Write a comment…" rows={2} autoFocus
              className="w-full text-xs border border-ui-border rounded-lg px-2 py-1.5 bg-ui-card text-ui-text resize-none focus:outline-none focus:ring-1 focus:ring-ui-ring/30" />
            <div className="flex justify-end">
              <button onClick={async () => {
                  if (!commentText.trim()) return;
                  await addComment({ taskId: task.task_id, content: commentText.trim() });
                  setCommentText(''); setShowCommentForm(false);
                }}
                disabled={!commentText.trim() || addingComment}
                className="text-xs px-2.5 h-6 rounded-lg btn-sheen text-on-primary font-semibold disabled:opacity-50 transition-colors">
                {addingComment ? '…' : 'Post'}
              </button>
            </div>
          </div>
        )}

        {comments.length > 0 && showComments && (
          <div className="mt-1 ml-[72px] space-y-1">
            {comments.map(c => (
              <div key={c.comment_id} className="text-xs bg-ui-subtle rounded-lg px-2 py-1.5">
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className="font-medium text-ui-text truncate">{c.author_name || 'Unknown'}</span>
                  <span className="text-ui-text3/50 shrink-0 whitespace-nowrap">
                    {new Date(c.created_on).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className="text-ui-text leading-snug whitespace-pre-wrap">{c.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Context link ─────────────────────────────────────────────────────────────

const ContextLink: React.FC<{ label: string; href: string | null; ctxType?: string | null; onNavigate: (href: string) => void; gray?: boolean }> = ({ label, href, ctxType, onNavigate, gray }) => {
  const chipCls = gray
    ? 'inline-flex items-center gap-1 text-xs text-ui-text3 border border-ui-border bg-ui-subtle rounded-full px-2 py-0.5 max-w-full'
    : 'inline-flex items-center gap-1 text-xs text-accent-text/70 hover:text-accent-text border border-accent/15 bg-accent-soft/20 hover:bg-accent-soft/40 rounded-full px-2 py-0.5 transition-colors group max-w-full';
  const inner = (
    <>
      <ContextTypeIcon type={ctxType} />
      <span className="truncate">{label}</span>
      {!gray && (
        <svg className="flex-shrink-0 opacity-50 group-hover:opacity-100" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
          <path d="M14 4h6v6M19.5 4.5 12 12"/><path d="M18 14.5V19a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H10"/>
        </svg>
      )}
    </>
  );
  if (!href || gray) return <div className="mt-1.5"><div className={chipCls}>{inner}</div></div>;
  return (
    <div className="mt-1.5">
      <button type="button" onClick={e => { e.stopPropagation(); onNavigate(href); }} onMouseDown={e => e.stopPropagation()} className={chipCls}>
        {inner}
      </button>
    </div>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  task: TaskFull;
  onSelect?: (taskId: string) => void;
  expandedId?: string | null;
  onExpand?: (id: string | null) => void;
  compact?: boolean;
  disabled?: boolean;
  lockedReason?: string;
  onStatusError?: (msg: string) => void;
  onDismissed?: (taskId: string) => void;
}

// ─── TaskCard ─────────────────────────────────────────────────────────────────

const TaskCard: React.FC<Props> = ({ task, onSelect, expandedId, onExpand, compact = false, disabled = false, lockedReason, onStatusError, onDismissed }) => {
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';
  const userRoleIds = useAppSelector((s) => s.sessionState.roles);
  const navigate = useNavigate();
  const [updateStatus, { isLoading: busy }] = useUpdateTaskStatusMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [claimTask, { isLoading: claiming }] = useClaimTaskMutation();

  const isClaimable = task.assignee_user_id === null && !!task.queue_role_id;
  // Only a member of the queue's role may claim it — the API rejects anyone else (403
  // TASK_QUEUE_ROLE_MISMATCH), so don't offer the button to them.
  const canClaim = isClaimable && !!task.queue_role_id && userRoleIds.includes(task.queue_role_id);
  const isClosed = task.status === 'completed' || task.status === 'cancelled';
  const isOpen = task.status === 'open' && !isClaimable && !disabled;

  const [localExpanded, setLocalExpanded] = useState(false);
  const showMore = onExpand ? expandedId === task.task_id : localExpanded;

  // Report this task to SAM as active_task while its detail is expanded — covers every manual
  // edit/action (status change, assign, follow, reminders) since each one refetches `task`, and
  // clears automatically (useSamSurfaceContext) when the card collapses or unmounts.
  useSamSurfaceContext('task', showMore ? {
    task_id: task.task_id,
    title: task.title,
    status: task.status,
    priority: task.priority,
    due_date: task.due_date ?? undefined,
    assignee_user_id: task.assignee_user_id ?? undefined,
    queue_role_id: task.queue_role_id ?? undefined,
    context: task.context ? { display_label: task.context.display_label } : null,
    description: task.description ?? undefined,
  } : null);

  useEffect(() => {
    if (showMore && onExpand && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMore]);

  const [dragX, setDragXState] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragXRef  = useRef(0);
  const startX    = useRef(0);
  const startY    = useRef(0);
  const moved     = useRef(false);
  const active    = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const priorityBtnRef = useRef<HTMLButtonElement>(null);
  const priorityMenuRef = useRef<HTMLDivElement>(null);
  const [priorityMenuStyle, setPriorityMenuStyle] = useState<CSSProperties>({});

  const [showTaskMenu, setShowTaskMenu] = useState(false);
  const taskMenuBtnRef = useRef<HTMLButtonElement>(null);
  const taskMenuRef = useRef<HTMLDivElement>(null);
  const [taskMenuStyle, setTaskMenuStyle] = useState<CSSProperties>({});

  useEffect(() => {
    if (!showPriorityMenu) return;
    const onMouse = (e: MouseEvent) => {
      if (!priorityMenuRef.current?.contains(e.target as Node) && !priorityBtnRef.current?.contains(e.target as Node)) setShowPriorityMenu(false);
    };
    const onScroll = () => setShowPriorityMenu(false);
    document.addEventListener('mousedown', onMouse);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showPriorityMenu]);

  const openPriorityMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    const btn = priorityBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setPriorityMenuStyle({
        position: 'fixed', zIndex: POPOVER_Z, minWidth: 110,
        left: rect.left,
        ...(spaceBelow < 120 ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    }
    setShowPriorityMenu(v => !v);
  };

  const handlePriorityChange = async (priority: string) => {
    setShowPriorityMenu(false);
    await updateTask({ id: task.task_id, body: { priority } });
  };

  useEffect(() => {
    if (!showTaskMenu) return;
    const onMouse = (e: MouseEvent) => {
      if (!taskMenuRef.current?.contains(e.target as Node) && !taskMenuBtnRef.current?.contains(e.target as Node)) setShowTaskMenu(false);
    };
    const onScroll = () => setShowTaskMenu(false);
    document.addEventListener('mousedown', onMouse);
    window.addEventListener('scroll', onScroll, true); // capture = catches any scrollable ancestor
    return () => {
      document.removeEventListener('mousedown', onMouse);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showTaskMenu]);

  const openTaskMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    const btn = taskMenuBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setTaskMenuStyle({
        position: 'fixed', zIndex: POPOVER_Z, minWidth: 150,
        left: Math.min(rect.right - 150, window.innerWidth - 158),
        ...(spaceBelow < 100 ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    }
    setShowTaskMenu(v => !v);
  };

  const setDragX = useCallback((x: number) => { dragXRef.current = x; setDragXState(x); }, []);

  // ── date ──────────────────────────────────────────────────────────────────
  const due = task.due_date ? new Date(task.due_date) : null;
  const dueInfo = due ? getDueDisplay(due, task.status) : null;

  // Rail color: gray if closed, red if overdue-open, blue if open
  const railColor = isClosed ? 'bg-ui-border3' : (dueInfo?.isOverdue ? 'bg-danger' : 'bg-accent');

  // Priority
  const pc = PRIORITY_CFG[task.priority as keyof typeof PRIORITY_CFG] ?? PRIORITY_CFG.normal;

  // Status
  const statusLabel = task.status === 'completed' ? 'COMPLETE' : task.status === 'cancelled' ? 'CLOSED' : 'OPEN';
  const statusCls = task.status === 'completed'
    ? 'bg-success/10 text-success-text'
    : task.status === 'cancelled'
    ? 'bg-ui-hover text-ui-text2'
    : 'bg-accent/10 text-accent-text';

  // ── actions ───────────────────────────────────────────────────────────────
  const resolveDrag = useCallback(() => {
    active.current = false;
    setDragging(false);
    const dx = dragXRef.current;
    if (dx >= THRESHOLD) {
      // Right swipe = complete
      setDragX(180);
      setTimeout(() => setDragX(0), 300);
      updateStatus({ id: task.task_id, status: 'completed' }).unwrap().then(() => onDismissed?.(task.task_id)).catch(e => onStatusError?.(String(e)));
    } else if (dx <= -THRESHOLD) {
      // Left swipe = close (cancel)
      setDragX(-180);
      setTimeout(() => setDragX(0), 300);
      updateStatus({ id: task.task_id, status: 'cancelled' }).unwrap().then(() => onDismissed?.(task.task_id)).catch(e => onStatusError?.(String(e)));
    } else {
      setDragX(0);
    }
  }, [task.task_id, updateStatus, setDragX, onStatusError, onDismissed]);

  const onTouchStart = useCallback((e: TouchEvent) => {
    if (!isOpen) return;
    active.current = true; moved.current = false;
    startX.current = e.touches[0].clientX; startY.current = e.touches[0].clientY;
    setDragging(true);
  }, [isOpen]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!active.current) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (!moved.current) {
      if (Math.abs(dy) > Math.abs(dx) + 5) {
        // Vertical scroll — abort horizontal tracking, never call preventDefault
        active.current = false; setDragging(false); setDragX(0); return;
      }
      if (Math.abs(dx) <= 5) return; // direction not clear yet, don't block scroll
      moved.current = true;
    }
    // Confirmed horizontal gesture — now it's safe to prevent scroll
    e.preventDefault();
    setDragX(Math.max(-140, Math.min(140, dx)));
  }, [setDragX]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   resolveDrag,  { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   resolveDrag);
    };
  }, [onTouchStart, onTouchMove, resolveDrag]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0 || !isOpen) return;
    active.current = true; moved.current = false; startX.current = e.clientX;
    setDragging(true);
    const onMove = (ev: MouseEvent) => {
      if (!active.current) return;
      const dx = ev.clientX - startX.current;
      if (Math.abs(dx) > 5) moved.current = true;
      setDragX(Math.max(-140, Math.min(140, dx)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      resolveDrag();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [isOpen, resolveDrag, setDragX]);

  const handleClick = () => {
    if (moved.current) { moved.current = false; return; }
    if (onSelect) {
      onSelect(task.task_id);
    } else if (onExpand) {
      onExpand(expandedId === task.task_id ? null : task.task_id);
    } else {
      setLocalExpanded(v => !v);
    }
  };

  const triggerClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDragX(-180);
    setTimeout(async () => {
      try { await updateStatus({ id: task.task_id, status: 'completed' }).unwrap(); onDismissed?.(task.task_id); }
      catch (err) { onStatusError?.(String(err)); }
      setDragX(0);
    }, 290);
  }, [task.task_id, updateStatus, setDragX, onStatusError, onDismissed]);

  const triggerCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDragX(180);
    setTimeout(async () => {
      try { await updateStatus({ id: task.task_id, status: 'cancelled' }).unwrap(); onDismissed?.(task.task_id); }
      catch (err) { onStatusError?.(String(err)); }
      setDragX(0);
    }, 290);
  }, [task.task_id, updateStatus, setDragX, onStatusError, onDismissed]);

  // leftP = progress of left swipe (0–1), rightP = right swipe
  const leftP  = dragX < 0 ? Math.min(1, -dragX / THRESHOLD) : 0;
  const rightP = dragX > 0 ? Math.min(1,  dragX / THRESHOLD) : 0;

  return (
    <div ref={containerRef} className="relative overflow-hidden rounded-2xl select-none touch-pan-y">

      {/* Right swipe = Complete */}
      <SwipeHint side="left" progress={rightP} tone="success" label="Complete" icon={<IcoCheck />} />

      {/* Left swipe = Close */}
      <SwipeHint
        side="right" progress={leftP} tone="danger" label="Close"
        icon={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        }
      />

      {/* ── Card ── */}
      <div onMouseDown={onMouseDown}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
        className={`card-workspace relative flex overflow-hidden ${showMore ? 'border-accent/25 shadow-pop' : ''}`}>

        {/* Busy overlay */}
        {busy && (
          <div className="absolute inset-0 z-10 rounded-2xl bg-ui-card/70 flex items-center justify-center">
            <svg className="animate-spin text-accent-text" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
            </svg>
          </div>
        )}

        {/* Colored left rail */}
        <div className={`flex-shrink-0 self-stretch rounded-l-2xl w-[3px] ${railColor}`} />

        {/* Body */}
        <div
          onClick={handleClick}
          className={`flex-1 min-w-0 ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5'} ${isClosed ? 'opacity-60' : ''} cursor-pointer`}
        >

          {/* ── Row 1: type icon · title · status badge · ⋮ menu ── */}
          <div className="flex items-start gap-1.5 mb-1.5">
            <TaskTypeIcon type={task.task_type} faded={isClosed} />
            <h3 className={`flex-1 font-semibold text-ui-text leading-snug min-w-0 ${compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-2'} ${isClosed ? 'line-through' : ''}`}>
              {task.title}
            </h3>

            {/* Lock icon (gate or permission denied) */}
            {(disabled || lockedReason) && task.status === 'open' && (
              <span title={lockedReason ?? 'You do not have permission to complete this task'}
                className="flex items-center justify-center rounded-full bg-danger/10 text-danger-text flex-shrink-0 w-5 h-5">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16" x2="12" y2="16.01"/>
                </svg>
              </span>
            )}

            {/* Status badge */}
            <span className={`font-bold rounded-full uppercase tracking-wide text-micro px-2.5 py-0.5 flex-shrink-0 ${statusCls}`}>
              {statusLabel}
            </span>

            {/* ⋮ task menu — only on open tasks, next to status badge */}
            {isOpen && (
              <button ref={taskMenuBtnRef} type="button"
                onMouseDown={e => e.stopPropagation()} onClick={openTaskMenu} title="Task actions"
                className={`flex items-center justify-center rounded-full flex-shrink-0 w-5 h-5 transition-colors ${showTaskMenu ? 'bg-accent/10 text-accent-text' : 'text-ui-text3 hover:bg-ui-hover hover:text-ui-text'}`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
            )}
          </div>

          {/* ── Row 2: overdue tag · due chip · [queue] · [reminders] · spacer · priority · chevron ── */}
          <div className="flex items-center gap-1.5 text-xs mb-0.5">
            {/* Overdue tag — kept alongside the due-date chip below (which also turns red), not
                in place of it: this is a separate, unmissable badge matching the same pattern
                already used for overdue tasks in the Calendar view (CalendarItem.tsx). */}
            {dueInfo?.isOverdue && (
              <span className="flex-shrink-0 font-bold rounded-full uppercase tracking-wide text-micro px-2.5 py-0.5 bg-danger text-white">
                Overdue
              </span>
            )}
            {/* Due date chip */}
            {dueInfo && (
              // min-w-0 (not flex-shrink-0): a long "Due … · in Nh" chip must truncate rather
              // than push the priority chip off the card on narrow screens. Overdue tasks show
              // only the relative time ("2h ago") — the OVERDUE badge above already says the rest.
              <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border min-w-0 ${
                dueInfo.isOverdue
                  ? 'bg-danger/10 border-danger/30 text-danger-text'
                  : dueInfo.main.toLowerCase().includes('today')
                    ? 'bg-accent-soft/30 border-accent/20 text-accent-text'
                    : 'bg-ui-subtle border-transparent text-ui-text3'
              }`}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <span className="truncate">
                  {dueInfo.isOverdue ? dueInfo.rel : (
                    <>
                      {dueInfo.main}
                      {dueInfo.rel && <span className="opacity-60"> · {dueInfo.rel}</span>}
                    </>
                  )}
                </span>
              </span>
            )}

            {/* Queue chip — only when assigned (not claimable), to show it came from a queue */}
            {task.queue_role_id && !isClaimable && (
              <span className="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-md bg-accent-soft/30 border border-accent/15 text-accent-text/70 flex-shrink-0">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Queue
              </span>
            )}

            {/* Reminder count (when not expanded) */}
            {task.active_reminder_count > 0 && !showMore && (
              <span className="text-xs text-warning-text flex items-center gap-0.5 flex-shrink-0">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 8a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14 18 8zM10.3 19a2 2 0 0 0 3.4 0"/>
                </svg>
                {task.active_reminder_count}
              </span>
            )}

            <div className="flex-1" />

            {/* Priority button */}
            <button ref={priorityBtnRef} type="button"
              onMouseDown={isClosed ? undefined : e => e.stopPropagation()}
              onClick={isClosed ? undefined : openPriorityMenu}
              disabled={isClosed}
              className={`flex items-center gap-1 text-xs font-semibold flex-shrink-0 px-1.5 py-0.5 rounded-lg border transition-colors ${isClosed ? 'text-ui-text3 cursor-default border-transparent' : `${pc.tone} border-current/20 hover:bg-ui-hover cursor-pointer`}`}>
              {(task.priority === 'high' || task.priority === 'urgent') && (
                <WarningTriangle />
              )}
              {pc.label}
            </button>

            {/* Expand chevron — only when not select-only */}
            {!compact && !onSelect && (
              <button type="button"
                onMouseDown={e => e.stopPropagation()}
                onClick={e => { e.stopPropagation(); handleClick(); }}
                className="flex items-center p-1 rounded text-ui-text3 hover:bg-black/5 flex-shrink-0 -mr-1">
                <IcoChevron open={showMore} />
              </button>
            )}
          </div>

          {/* ── Row 3: context link ── */}
          {!compact && task.context?.display_label && (
            <ContextLink
              label={task.context.display_label}
              href={task.context.entity_id ? (ENTITY_ROUTE[task.context.context_type]?.(task.context.entity_id) ?? null) : null}
              ctxType={task.context.context_type}
              onNavigate={href => { navigate(href); }}
              gray={isClosed}
            />
          )}

          {/* ── Claim button — visible in both folded and expanded state ── */}
          {isClaimable && (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xs text-ui-text3 flex items-center gap-1">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                {task.queue_role_name ? `${task.queue_role_name} Queue` : (canClaim ? 'In your role queue' : 'Role queue')}
              </span>
              {canClaim && (
              <button type="button"
                onClick={e => { e.stopPropagation(); claimTask(task.task_id); }}
                disabled={claiming}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg btn-sheen text-on-primary text-xs font-semibold transition-colors disabled:opacity-50">
                {claiming ? (
                  <svg className="animate-spin" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
                {claiming ? 'Claiming…' : 'Claim'}
              </button>
              )}
            </div>
          )}

          {/* ── Expanded detail ── */}
          {showMore && !onSelect && (
            <TaskInlineDetail task={task} currentUserId={currentUserId} isClosed={isClosed} />
          )}
        </div>
      </div>

      {/* Priority picker portal */}
      {showPriorityMenu && createPortal(
        <div ref={priorityMenuRef} style={priorityMenuStyle}
          className="bg-ui-card border border-ui-border rounded-xl shadow-pop py-1.5 overflow-hidden">
          <div className="px-3 pb-1 text-micro font-bold text-ui-text3 uppercase tracking-wider">Priority</div>
          {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
            <button key={key} type="button" onClick={() => handlePriorityChange(key)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-ui-hover transition-colors ${cfg.tone} ${task.priority === key ? 'font-semibold' : ''}`}>
              <span className="w-4 flex items-center justify-center">
                {key === 'low'    && <span className="w-2 h-2 rounded-full bg-ui-border inline-block" />}
                {key === 'normal' && <span className="w-2 h-2 rounded-full bg-ui-border3 inline-block" />}
                {key === 'high'   && <WarningTriangle />}
                {key === 'urgent' && <WarningTriangle />}
              </span>
              {cfg.label}
              {task.priority === key && <span className="ml-auto text-xs opacity-60">✓</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}

      {/* Task action menu portal */}
      {showTaskMenu && createPortal(
        <div ref={taskMenuRef} style={taskMenuStyle}
          className="rounded-xl border border-ui-border bg-ui-card shadow-pop overflow-hidden py-1">
          <button type="button"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => { setShowTaskMenu(false); triggerClose(e); }}
            className="flex w-full items-center gap-2 px-3 py-[3px] text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-success-text flex-shrink-0">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Complete
          </button>
          <button type="button"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => { setShowTaskMenu(false); triggerCancel(e); }}
            className="flex w-full items-center gap-2 px-3 py-[3px] text-xs font-medium text-ui-text hover:bg-ui-hover transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-danger-text flex-shrink-0">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Close
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default TaskCard;

// Re-export legacy names used by other components
export { TaskCard };
export const IcoCalendar = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 mt-0.5 ${className ?? ''}`}>
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
export const IcoTaskCheck = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 mt-0.5 ${className ?? ''}`}>
    <polyline points="9 11 12 14 22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);
export const STATUS_CFG = {
  open:      { bg: 'bg-accent/10',  text: 'text-accent-text',  label: 'Open' },
  completed: { bg: 'bg-ui-hover', text: 'text-ui-text2',  label: 'Closed' },
  cancelled: { bg: 'bg-ui-hover', text: 'text-ui-text2',  label: 'Closed' },
} as const;
